import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideLocalizedRoutes } from './app.routes';
import { LocalizedAppRoutes } from './localized-app-routes';
import { KundeModulesService } from './global/services/kunde-modules.service';
import { MeinProfilFunktionenRowComponent } from './features/mein-profil/components/mein-profil-funktionen-row/mein-profil-funktionen-row.component';
import { provideStore } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApiTokenInterceptor } from '@lexplus-authentication';
import { API_CONFIGURATION, APP_ENVIRONMENT, environmentUser } from '@lexplus-environments';
import {
  ColorSchemeService,
  ImpersonationState,
  LOCALIZED_APP_ROUTES,
  MEIN_PROFIL_PERSONAL_EXTRA_ROWS,
  ProfileState,
  provideDmLabels,
  SupportWidgetService,
  TemporarySessionState,
  toFilledGlyph,
  withSessionViewTransitions,
} from '@lexplus-shared-lib';
import { LOCAL_STORAGE_ENGINE, SESSION_STORAGE_ENGINE, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideDmIcons } from '@digitale-menschen/component-library-angular';
import {
  AlertCircle,
  AlertTriangle,
  Annotation,
  Archive,
  ArrowDown,
  ArrowNarrowUpRight,
  ArrowRight,
  ArrowsUp,
  ArrowUpRight,
  BarChartSquare02,
  Bell01,
  BookOpen01,
  Box,
  Briefcase01,
  Building02,
  Calendar,
  CalendarCheck02,
  Check,
  CheckCircle,
  CheckDone01,
  CheckSquare,
  CheckVerified03,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  ClipboardPlus,
  Clock,
  ClockRewind,
  CornerDownRight,
  DotsHorizontal,
  Download04,
  DownloadCloud02,
  Edit01,
  Edit02,
  Edit05,
  Equal,
  EyeOff,
  File04,
  File06,
  FileCheck02,
  FileQuestion02,
  FileSearch02,
  FilterLines,
  Flag01,
  FlipBackward,
  GitBranch01,
  Globe01,
  Hash02,
  HelpCircle,
  InfoCircle,
  LayoutAlt03,
  LayoutLeft,
  LifeBuoy01,
  Link01,
  Link03,
  LinkBroken01,
  LinkExternal02,
  List,
  LogOut01,
  Mail01,
  MarkerPin01,
  MarkerPin06,
  Menu02,
  MessageAlertCircle,
  MinusCircle,
  Monitor01,
  Moon01,
  Package,
  PieChart03,
  Pin01,
  Plus,
  PlusCircle,
  RefreshCcw01,
  RefreshCw01,
  RefreshCw02,
  Save01,
  Scales01,
  SearchLg,
  Send01,
  Settings02,
  Sliders04,
  Star01,
  Sun,
  SwitchVertical01,
  Tag01,
  Target04,
  Trash01,
  UploadCloud02,
  User01,
  User03,
  Users01,
  XCircle,
  XClose,
} from '@digitale-menschen/component-library/untitled-icons';

export const appConfig: ApplicationConfig = {
  providers: [
    // Zoneless is Angular's default; declared explicitly so the app never silently
    // picks up a different change-detection mode from a framework default.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    // The routes themselves come from provideLocalizedRoutes(); route data reaches the
    // component as inputs (isForAdmin and the like). `computed` puts the browser back where it
    // was when a guard refuses a back or forward — a detail drawer asking about unsaved changes —
    // which holds only while the Router owns the history state.
    provideRouter(
      [],
      withComponentInputBinding(),
      withRouterConfig({ canceledNavigationResolution: 'computed' }),
      withSessionViewTransitions(),
    ),
    provideLocalizedRoutes(),
    provideHttpClient(withInterceptorsFromDi()),
    // Every icon the app renders, in one place: a name that is not here renders
    // nothing at all, with no warning.
    provideDmIcons({
      'alert-circle': AlertCircle,
      'alert-triangle': AlertTriangle,
      // The «Archiviert» badge on an Archiv card.
      archive: Archive,
      'arrow-down': ArrowDown,
      'arrow-narrow-up-right': ArrowNarrowUpRight,
      'arrow-right': ArrowRight,
      'arrow-up-right': ArrowUpRight,
      'arrows-up': ArrowsUp,
      'bar-chart-square-02': BarChartSquare02,
      'book-open-01': BookOpen01,
      // The Herausgeber on an Erlass card: an institution, not the law it issued, which is
      // what `scales-01` says on the Anforderung card's Erlass band.
      'building-02': Building02,
      box: Box,
      // `dm-date-picker` renders this in its own trigger; the design's Inkrafttreten field
      // shows it. Nothing here passes it as a prop, so an unregistered name left the
      // trigger's icon slot empty.
      calendar: Calendar,
      'calendar-check-02': CalendarCheck02,
      // `dm-select-item`'s selection indicator, for both the checkmark and the checkbox
      // style — `renderCheckGlyph()` is the same glyph in both. Every combobox in the app
      // draws it, so without it a selected option shows an empty box.
      check: Check,
      'check-circle': CheckCircle,
      'check-done-01': CheckDone01,
      'check-square': CheckSquare,
      'check-verified-03': CheckVerified03,
      'chevron-down': ChevronDown,
      'chevron-left': ChevronLeft,
      'chevron-right': ChevronRight,
      'chevron-up': ChevronUp,
      'clipboard-plus': ClipboardPlus,
      'clock-rewind': ClockRewind,
      'corner-down-right': CornerDownRight,
      'dots-horizontal': DotsHorizontal,
      'download-04': Download04,
      'download-cloud-02': DownloadCloud02,
      'edit-01': Edit01,
      'edit-02': Edit02,
      'edit-05': Edit05,
      'eye-off': EyeOff,
      'file-04': File04,
      'file-06': File06,
      'file-check-02': FileCheck02,
      'file-question-02': FileQuestion02,
      'filter-lines': FilterLines,
      'file-search-02': FileSearch02,
      'flag-01': Flag01,
      'flip-backward': FlipBackward,
      'git-branch-01': GitBranch01,
      'globe-01': Globe01,
      'moon-01': Moon01,
      'monitor-01': Monitor01,
      sun: Sun,
      'hash-02': Hash02,
      'help-circle': HelpCircle,
      'info-circle': InfoCircle,
      'layout-alt-03': LayoutAlt03,
      'layout-left': LayoutLeft,
      'life-buoy-01': LifeBuoy01,
      'link-01': Link01,
      'link-03': Link03,
      'link-broken-01': LinkBroken01,
      'link-external-02': LinkExternal02,
      list: List,
      'log-out-01': LogOut01,
      'mail-01': Mail01,
      'marker-pin-01': MarkerPin01,
      'marker-pin-06': MarkerPin06,
      'menu-02': Menu02,
      'message-alert-circle': MessageAlertCircle,
      'minus-circle': MinusCircle,
      package: Package,
      'bell-01': Bell01,
      'briefcase-01': Briefcase01,
      'pie-chart-03': PieChart03,
      'pin-01': Pin01,
      'plus-circle': PlusCircle,
      'refresh-ccw-01': RefreshCcw01,
      'refresh-cw-01': RefreshCw01,
      'refresh-cw-02': RefreshCw02,
      'save-01': Save01,
      'scales-01': Scales01,
      'search-lg': SearchLg,
      'send-01': Send01,
      'settings-02': Settings02,
      'sliders-04': Sliders04,
      'star-01': Star01,
      // A set favourite is a filled star; the set ships the outline one only.
      'star-01-filled': toFilledGlyph(Star01),
      'switch-vertical-01': SwitchVertical01,
      'tag-01': Tag01,
      'target-04': Target04,
      'trash-01': Trash01,
      'upload-cloud-02': UploadCloud02,
      'user-01': User01,
      'user-03': User03,
      'users-01': Users01,
      // Clears the search field and marks a removed filter chip on «Dokumenten Manager»
      // and «Aufgaben»; both rendered nothing there until it was registered here.
      'x-circle': XCircle,
      // The clear affordance of `dm-combobox` (`showClear`) and of `dm-date-picker`; both
      // render it themselves, so an unregistered name left a clickable but empty button.
      'x-close': XClose,
      annotation: Annotation,
      circle: Circle,
      clock: Clock,
      equal: Equal,
      plus: Plus,
    }),
    provideStore(
      [ProfileState, TemporarySessionState, ImpersonationState],
      withNgxsStoragePlugin({
        keys: [
          // Durable, non-critical → localStorage.
          { key: ProfileState, engine: LOCAL_STORAGE_ENGINE },
          // Short-lived login flow including passwordChangeToken → sessionStorage only
          // (survives a reload, disappears when the tab closes).
          { key: TemporarySessionState, engine: SESSION_STORAGE_ENGINE },
          // An active impersonation should survive a reload → localStorage.
          { key: ImpersonationState, engine: LOCAL_STORAGE_ENGINE },
        ],
      }),
    ),
    // Every dm-* label family the library ships in English; a family left unset renders English.
    provideDmLabels(),
    // The support widget loads on every page, signed in or not: a user who cannot get
    // past the sign-in screen has to be able to report exactly that.
    provideAppInitializer(() => inject(ColorSchemeService).initialize()),
    provideAppInitializer(() => inject(SupportWidgetService).initialize()),
    provideAppInitializer(() => inject(KundeModulesService).refresh()),
    {
      provide: API_CONFIGURATION,
      useValue: environmentUser.apiConfiguration,
    },
    {
      provide: APP_ENVIRONMENT,
      useValue: environmentUser,
    },
    { provide: MEIN_PROFIL_PERSONAL_EXTRA_ROWS, useValue: MeinProfilFunktionenRowComponent },
    {
      provide: LOCALIZED_APP_ROUTES,
      useValue: LocalizedAppRoutes,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiTokenInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
  ],
};
